import React, { useState, useEffect, useRef, useMemo } from "react";
import moment from "moment";
import { getAdminSchools, getAdminAdLevelReport } from "@/actions/reportsAdmin";
import { Layers, Search, School, Download, Facebook, Database, AlertTriangle, CheckCircle, Info } from "lucide-react";
import ReportHeader from "../components/ReportHeader";

interface SchoolOption {
  slate_id: number;
  slate_name: string;
  cityName: string;
  areaName: string;
}

interface StatusOption {
  opt_id: number;
  opt_name: string;
}

interface MetaAdEntry {
  campaign_id: string;
  adset_id: string;
  ad_id: string;
  meta_leads: number;
}

interface AdRow {
  campaign_id: string;
  campaign_name: string;
  adset_id: string;
  adset_name: string;
  ad_id: string;
  ad_name: string;
  total: number;
  [key: string]: string | number;
}

interface OrganicData {
  total: number;
  admin_status_counts: Record<number, number>;
  cross_tab: Record<number, Record<number, number>>;
}

interface ReportData {
  data: AdRow[];
  meta_ad_data: Record<string, MetaAdEntry>;
  admin_statuses: StatusOption[];
  client_statuses: StatusOption[];
  cross_tab: Record<string, Record<number, Record<number, number>>>;
  organic_data: OrganicData;
}

type GroupedRow = {
  type: "campaign" | "adset" | "ad" | "client_status" | "grand_total" | "organic" | "organic_status";
  name: string;
  total: number;
  metaLeads: number;
  statusCounts: Record<string, number>;
  groupedCounts: Record<string, number>;
  campaign_id?: string;
  adset_id?: string;
  ad_id?: string;
  count?: number;
  groupKey?: string;
  parentStatusCounts?: Record<string, number>;
  clientStatusId?: number;
  adId?: string;
};

// Status grouping - exact match by lowercase status name
const QUALIFIED_STATUSES = new Set([
  "admitted", "application form taken", "visited", "assigned to client",
  "school business lead", "will visit", "will visit but not visited",
  "will visit but not interested", "yet to decide", "enquiry form submitted",
  "verified", "may visit campus",
]);
const SEMI_QUALIFIED_STATUSES = new Set([
  "assigned to claimed",
  "follow up 1", "follow up 2", "follow up 3",
  "follow up1", "follow up2", "follow up3",
  "call back", "callback",
  "multiple options",
  "pending", "waiting for email response",
]);
const NO_RESPONSE_STATUSES = new Set([
  "no response", "no response 1", "no response 2", "no response 3",
  "no response1", "no response2", "no response3",
  "not answered",
]);
const NOT_QUALIFIED_STATUSES = new Set([
  "distance", "fee", "call denied", "hostel", "day school", "curriculum issue",
  "food", "transport issue", "couldn't assign", "only in this school",
  "not enquired", "invalid number", "admitted to other school", "admitted in other school",
  "no seat available", "no seats available", "employment", "vendor", "duplicate",
  "call not connecting", "not interested", "school is far", "change of plan",
  "completed", "leads sent", "others",
]);
const NEW_LEAD_STATUSES = new Set(["new lead"]);

const STATUS_GROUPS = [
  { key: "new_lead", label: "New Lead", color: "text-blue-700 dark:text-blue-400", headerBg: "bg-blue-50 dark:bg-blue-900/20" },
  { key: "qualified", label: "Qualified", color: "text-green-700 dark:text-green-400", headerBg: "bg-green-50 dark:bg-green-900/20" },
  { key: "semi_qualified", label: "Semi Qualified", color: "text-amber-700 dark:text-amber-400", headerBg: "bg-amber-50 dark:bg-amber-900/20" },
  { key: "no_response", label: "No Response", color: "text-blue-600 dark:text-blue-400", headerBg: "bg-blue-50 dark:bg-blue-900/10" },
  { key: "not_qualified", label: "Not Qualified", color: "text-red-700 dark:text-red-400", headerBg: "bg-red-50 dark:bg-red-900/20" },
];

const classifyStatus = (name: string): string => {
  const lower = name.toLowerCase().trim();
  if (NEW_LEAD_STATUSES.has(lower)) return "new_lead";
  if (QUALIFIED_STATUSES.has(lower)) return "qualified";
  if (SEMI_QUALIFIED_STATUSES.has(lower)) return "semi_qualified";
  if (NO_RESPONSE_STATUSES.has(lower)) return "no_response";
  if (NOT_QUALIFIED_STATUSES.has(lower)) return "not_qualified";
  return "not_qualified";
};

const formatNumber = (num: number): string => num?.toLocaleString() || "0";

const AdLevelReport: React.FC = () => {
  const [fromDate, setFromDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; groupKey: string; groupLabel: string; color: string; breakdown: { name: string; count: number }[] } | null>(null);

  useEffect(() => {
    fetchSchools();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSchools = async () => {
    try {
      const response = await getAdminSchools();
      if (response.success) setSchools((response.data as SchoolOption[]) || []);
    } catch (error) {
      console.error("Error fetching schools:", error);
    }
  };

  const fetchData = async () => {
    if (!selectedSchool) return;
    setLoading(true);
    try {
      const response = await getAdminAdLevelReport({
        from_date: fromDate,
        to_date: toDate,
        slate_id: selectedSchool.slate_id,
      });
      if (response.success) {
        setReportData(response as unknown as ReportData);
      }
    } catch (error) {
      console.error("Error fetching ad level report:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSchools = schools.filter(
    (school) =>
      school.slate_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (school.cityName && school.cityName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (school.areaName && school.areaName.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleSelectSchool = (school: SchoolOption) => {
    setSelectedSchool(school);
    const displayText = [school.slate_name, school.areaName, school.cityName].filter(Boolean).join(" • ");
    setSearchQuery(displayText);
    setShowSuggestions(false);
  };

  // Build admin status -> group mapping
  const adminStatusGroupMap = useMemo(() => {
    const map: Record<number, string> = {};
    (reportData?.admin_statuses || []).forEach((s) => {
      map[s.opt_id] = classifyStatus(s.opt_name);
    });
    return map;
  }, [reportData?.admin_statuses]);

  // Compute grouped counts from statusCounts
  const computeGroupedCounts = (statusCounts: Record<string, number>): Record<string, number> => {
    const grouped: Record<string, number> = { new_lead: 0, qualified: 0, semi_qualified: 0, no_response: 0, not_qualified: 0 };
    Object.entries(statusCounts).forEach(([key, val]) => {
      if (!key.startsWith("admin_")) return;
      const optId = parseInt(key.replace("admin_", ""));
      const group = adminStatusGroupMap[optId] || "new_lead";
      grouped[group] += val;
    });
    return grouped;
  };

  // Compute meta leads per ad/adset/campaign from meta_ad_data
  const metaMap = useMemo(() => {
    const map = { byAd: {} as Record<string, number>, byAdset: {} as Record<string, number>, byCampaign: {} as Record<string, number>, total: 0 };
    if (!reportData?.meta_ad_data) return map;
    Object.values(reportData.meta_ad_data).forEach((entry) => {
      map.byAd[entry.ad_id] = (map.byAd[entry.ad_id] || 0) + entry.meta_leads;
      map.byAdset[entry.adset_id] = (map.byAdset[entry.adset_id] || 0) + entry.meta_leads;
      map.byCampaign[entry.campaign_id] = (map.byCampaign[entry.campaign_id] || 0) + entry.meta_leads;
      map.total += entry.meta_leads;
    });
    return map;
  }, [reportData?.meta_ad_data]);

  // Build grouped rows: campaign > adset > ad > client_status hierarchy
  const groupedRows = useMemo((): GroupedRow[] => {
    if (!reportData?.data?.length) return [];

    const rows: GroupedRow[] = [];
    const data = reportData.data;
    const statusKeys = [
      ...reportData.admin_statuses.map((s) => `admin_${s.opt_id}`),
      ...reportData.client_statuses.map((s) => `client_${s.opt_id}`),
    ];

    const sumCounts = (items: AdRow[]): Record<string, number> => {
      const counts: Record<string, number> = {};
      statusKeys.forEach((key) => {
        counts[key] = items.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
      });
      return counts;
    };

    // Group by campaign
    const campaigns = new Map<string, AdRow[]>();
    data.forEach((row) => {
      const key = row.campaign_id;
      if (!campaigns.has(key)) campaigns.set(key, []);
      campaigns.get(key)!.push(row);
    });

    campaigns.forEach((campaignRows, campaignId) => {
      const campaignTotal = campaignRows.reduce((sum, r) => sum + Number(r.total), 0);
      const campaignCounts = sumCounts(campaignRows);

      const searchLower = tableSearch.toLowerCase();
      if (tableSearch) {
        const hasMatch = campaignRows.some(
          (r) =>
            r.campaign_name.toLowerCase().includes(searchLower) ||
            r.adset_name.toLowerCase().includes(searchLower) ||
            r.ad_name.toLowerCase().includes(searchLower)
        );
        if (!hasMatch) return;
      }

      rows.push({
        type: "campaign",
        name: campaignRows[0].campaign_name,
        total: campaignTotal,
        metaLeads: metaMap.byCampaign[campaignId] || 0,
        statusCounts: campaignCounts,
        groupedCounts: computeGroupedCounts(campaignCounts),
        campaign_id: campaignId,
      });

      // Group by adset within campaign
      const adsets = new Map<string, AdRow[]>();
      campaignRows.forEach((row) => {
        const key = row.adset_id;
        if (!adsets.has(key)) adsets.set(key, []);
        adsets.get(key)!.push(row);
      });

      adsets.forEach((adsetRows, adsetId) => {
        const adsetTotal = adsetRows.reduce((sum, r) => sum + Number(r.total), 0);
        const adsetCounts = sumCounts(adsetRows);

        if (tableSearch) {
          const hasMatch = adsetRows.some(
            (r) =>
              r.campaign_name.toLowerCase().includes(searchLower) ||
              r.adset_name.toLowerCase().includes(searchLower) ||
              r.ad_name.toLowerCase().includes(searchLower)
          );
          if (!hasMatch) return;
        }

        rows.push({
          type: "adset",
          name: adsetRows[0].adset_name,
          total: adsetTotal,
          metaLeads: metaMap.byAdset[adsetId] || 0,
          statusCounts: adsetCounts,
          groupedCounts: computeGroupedCounts(adsetCounts),
          adset_id: adsetId,
        });

        adsetRows.forEach((adRow) => {
          if (
            tableSearch &&
            !adRow.campaign_name.toLowerCase().includes(searchLower) &&
            !adRow.adset_name.toLowerCase().includes(searchLower) &&
            !adRow.ad_name.toLowerCase().includes(searchLower)
          ) {
            return;
          }

          const adCounts: Record<string, number> = {};
          statusKeys.forEach((key) => {
            adCounts[key] = Number(adRow[key]) || 0;
          });

          rows.push({
            type: "ad",
            name: adRow.ad_name,
            total: Number(adRow.total),
            metaLeads: metaMap.byAd[adRow.ad_id] || 0,
            statusCounts: adCounts,
            groupedCounts: computeGroupedCounts(adCounts),
            ad_id: adRow.ad_id,
          });

          // Client status sub-rows flat under each ad, distributed across admin status group columns
          reportData.client_statuses.forEach((cs) => {
            const count = Number(adRow[`client_${cs.opt_id}`]) || 0;
            if (count > 0) {
              // Compute grouped counts from cross_tab admin status breakdown
              const csGroupedCounts: Record<string, number> = { new_lead: 0, qualified: 0, semi_qualified: 0, no_response: 0, not_qualified: 0 };
              const adTab = reportData.cross_tab?.[adRow.ad_id] || {};
              const statusTab = adTab[cs.opt_id] || {};
              Object.entries(statusTab).forEach(([adminId, cnt]) => {
                if (!cnt) return;
                if (adminId === '0') {
                  csGroupedCounts["new_lead"] += cnt as number;
                  return;
                }
                const group = adminStatusGroupMap[parseInt(adminId)] || "new_lead";
                csGroupedCounts[group] += cnt as number;
              });
              rows.push({
                type: "client_status",
                name: cs.opt_name,
                total: 0,
                metaLeads: 0,
                statusCounts: {},
                groupedCounts: csGroupedCounts,
                count,
                groupKey: classifyStatus(cs.opt_name),
                parentStatusCounts: adCounts,
                clientStatusId: cs.opt_id,
                adId: adRow.ad_id,
              });
            }
          });
        });
      });
    });

    // Organic section (non-FB leads)
    const organic = reportData.organic_data;
    if (organic && organic.total > 0) {
      // Compute grouped counts for organic from admin_status_counts
      const organicGrouped: Record<string, number> = { new_lead: 0, qualified: 0, semi_qualified: 0, no_response: 0, not_qualified: 0 };
      Object.entries(organic.admin_status_counts).forEach(([adminId, cnt]) => {
        if (adminId === '0') {
          organicGrouped["new_lead"] += cnt;
          return;
        }
        const group = adminStatusGroupMap[parseInt(adminId)] || "new_lead";
        organicGrouped[group] += cnt;
      });

      rows.push({
        type: "organic",
        name: "Organic",
        total: organic.total,
        metaLeads: 0,
        statusCounts: {},
        groupedCounts: organicGrouped,
      });

      // Client status sub-rows under Organic (mirrors FB ad client_status rows)
      reportData.client_statuses.forEach((cs) => {
        // Sum count for this client status across all admin statuses
        const statusTab = organic.cross_tab?.[cs.opt_id] || {};
        const count = Object.values(statusTab).reduce((sum, cnt) => sum + cnt, 0);
        if (count > 0) {
          // Compute grouped counts from cross_tab admin status breakdown
          const csGroupedCounts: Record<string, number> = { new_lead: 0, qualified: 0, semi_qualified: 0, no_response: 0, not_qualified: 0 };
          Object.entries(statusTab).forEach(([adminId, cnt]) => {
            if (!cnt) return;
            if (adminId === '0') {
              csGroupedCounts["new_lead"] += cnt as number;
              return;
            }
            const group = adminStatusGroupMap[parseInt(adminId)] || "new_lead";
            csGroupedCounts[group] += cnt as number;
          });
          rows.push({
            type: "organic_status",
            name: cs.opt_name,
            total: 0,
            metaLeads: 0,
            statusCounts: {},
            groupedCounts: csGroupedCounts,
            count,
            clientStatusId: cs.opt_id,
          });
        }
      });
    }

    // Grand total (includes organic)
    const hasRows = rows.length > 0;
    if (hasRows) {
      const fbTotal = data.reduce((sum, r) => sum + Number(r.total), 0);
      const organicTotal = organic?.total || 0;
      const grandTotal = fbTotal + organicTotal;
      const grandCounts = sumCounts(data);

      // Merge organic admin status counts into grand counts for grouped display
      const grandGrouped = computeGroupedCounts(grandCounts);
      if (organic && organic.total > 0) {
        Object.entries(organic.admin_status_counts).forEach(([adminId, cnt]) => {
          if (adminId === '0') {
            grandGrouped["new_lead"] += cnt;
            return;
          }
          const group = adminStatusGroupMap[parseInt(adminId)] || "new_lead";
          grandGrouped[group] += cnt;
        });
      }

      rows.push({
        type: "grand_total",
        name: "Grand Total",
        total: grandTotal,
        metaLeads: metaMap.total,
        statusCounts: grandCounts,
        groupedCounts: grandGrouped,
      });
    }

    return rows;
  }, [reportData, tableSearch, metaMap, adminStatusGroupMap]);

  // Summary stats
  const summaryStats = useMemo(() => {
    if (!reportData?.data?.length && !reportData?.organic_data?.total) return null;
    const fbTotal = reportData.data.reduce((sum, r) => sum + Number(r.total), 0);
    const organicTotal = reportData.organic_data?.total || 0;
    const crmTotal = fbTotal + organicTotal;
    const metaTotal = metaMap.total;
    const missing = Math.max(0, metaTotal - fbTotal);
    const matchPct = metaTotal > 0 ? ((fbTotal / metaTotal) * 100) : 0;
    return { metaTotal, crmTotal, missing, matchPct, organicTotal };
  }, [reportData, metaMap]);

  // CSV export - matches table layout exactly
  const handleExport = () => {
    if (!groupedRows.length) return;

    const headers = [
      "Name",
      ...(hasMetaData ? ["Meta"] : []),
      "CRM",
      ...(hasMetaData ? ["Miss"] : []),
      ...STATUS_GROUPS.map((g) => g.label),
    ];

    const csvRows = [headers.join(",")];

    groupedRows.forEach((row) => {
      if (row.type === "client_status") {
        // Client status: name indented, count in CRM, admin status grouped counts in group columns
        const groupCols = STATUS_GROUPS.map((g) => row.groupedCounts[g.key] || "");
        const values = [
          `"    ${row.name.replace(/"/g, '""')}"`,
          ...(hasMetaData ? [""] : []),
          row.count || 0,
          ...(hasMetaData ? [""] : []),
          ...groupCols,
        ];
        csvRows.push(values.join(","));
        return;
      }

      if (row.type === "organic_status") {
        // Organic client status sub-row: indented name, count in CRM, grouped counts in group columns
        const groupCols = STATUS_GROUPS.map((g) => row.groupedCounts[g.key] || "");
        const values = [
          `"  ${row.name.replace(/"/g, '""')}"`,
          ...(hasMetaData ? [""] : []),
          row.count || 0,
          ...(hasMetaData ? [""] : []),
          ...groupCols,
        ];
        csvRows.push(values.join(","));
        return;
      }

      // Campaign / AdSet / Ad / Organic / Grand Total
      const showGrouped = row.type === "organic" || row.type === "grand_total";
      const indent = row.type === "adset" ? "  " : row.type === "ad" ? "    " : "";
      const missing = Math.max(0, row.metaLeads - row.total);
      const values = [
        `"${indent}${row.name.replace(/"/g, '""')}"`,
        ...(hasMetaData ? [row.metaLeads > 0 ? row.metaLeads : ""] : []),
        row.total,
        ...(hasMetaData ? [row.type === "organic" ? "" : missing > 0 ? missing : ""] : []),
        ...STATUS_GROUPS.map((g) => showGrouped ? (row.groupedCounts[g.key] || "") : ""),
      ];
      csvRows.push(values.join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ad-level-report-${selectedSchool?.slate_name || "school"}-${fromDate}-${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const searchContent = (
    <div ref={searchRef} className="relative w-96">
      <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 w-full">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowSuggestions(true);
            if (!e.target.value) setSelectedSchool(null);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder="Search school..."
          className="border-none bg-transparent focus:ring-0 text-sm text-gray-700 dark:text-gray-300 flex-1 outline-none"
        />
        <button
          onClick={fetchData}
          disabled={!selectedSchool}
          className="px-3 py-1 bg-brandColor text-white text-sm rounded-md hover:bg-brandColorDark disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Search
        </button>
      </div>
      {showSuggestions && searchQuery && filteredSchools.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
          {filteredSchools.slice(0, 10).map((school) => (
            <div
              key={school.slate_id}
              onClick={() => handleSelectSchool(school)}
              className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer text-sm"
            >
              <div className="font-medium text-gray-900 dark:text-white">{school.slate_name}</div>
              {(school.areaName || school.cityName) && (
                <div className="text-xs text-gray-500">
                  {[school.areaName, school.cityName].filter(Boolean).join(", ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const hasMetaData = metaMap.total > 0;

  // Returns admin status breakdown for a specific (ad_id, client_status_id) from cross_tab
  const getCrossTabBreakdown = (adId: string, clientStatusId: number) => {
    const adTab = reportData?.cross_tab?.[adId] || {};
    const statusTab = adTab[clientStatusId] || {};
    return Object.entries(statusTab)
      .filter(([, cnt]) => cnt > 0)
      .map(([adminId, cnt]) => {
        if (adminId === '0') return { name: "No Status", count: cnt as number };
        const adminStatus = reportData?.admin_statuses.find((s) => s.opt_id === parseInt(adminId));
        return { name: adminStatus?.opt_name || `Status ${adminId}`, count: cnt as number };
      })
      .sort((a, b) => b.count - a.count);
  };

  // Returns admin status breakdown for organic data (no ad_id, just client_status_id)
  const getOrganicCrossTabBreakdown = (clientStatusId: number) => {
    const statusTab = reportData?.organic_data?.cross_tab?.[clientStatusId] || {};
    return Object.entries(statusTab)
      .filter(([, cnt]) => cnt > 0)
      .map(([adminId, cnt]) => {
        if (adminId === '0') return { name: "No Status", count: cnt as number };
        const adminStatus = reportData?.admin_statuses.find((s) => s.opt_id === parseInt(adminId));
        return { name: adminStatus?.opt_name || `Status ${adminId}`, count: cnt as number };
      })
      .sort((a, b) => b.count - a.count);
  };

  return (
    <div className="h-full flex flex-col">
      <ReportHeader
        title="Ad Level Report"
        subtitle="Campaign / AdSet / Ad status breakdown"
        icon={<Layers className="h-5 w-5 text-white" />}
        fromDate={fromDate}
        toDate={toDate}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        onApplyFilter={fetchData}
        beforeDateContent={searchContent}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {!selectedSchool ? (
          <div className="text-center py-8">
            <School className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Search and select a school to view the ad level report</p>
          </div>
        ) : loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandColor"></div>
          </div>
        ) : !reportData?.data?.length && !reportData?.organic_data?.total ? (
          <div className="text-center py-8">
            <School className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No lead data found for this school in the selected date range</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary Cards */}
            {summaryStats && (
              <div className="grid grid-cols-5 gap-3">
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 flex items-center gap-3">
                  <Facebook className="h-5 w-5 text-blue-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Meta Leads</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{formatNumber(summaryStats.metaTotal)}</p>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 flex items-center gap-3">
                  <Database className="h-5 w-5 text-indigo-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">CRM Leads</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">{formatNumber(summaryStats.crmTotal)}</p>
                  </div>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg px-4 py-3 flex items-center gap-3">
                  <Layers className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Organic</p>
                    <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{formatNumber(summaryStats.organicTotal)}</p>
                  </div>
                </div>
                <div className={`border rounded-lg px-4 py-3 flex items-center gap-3 ${summaryStats.missing > 0 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700'}`}>
                  <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${summaryStats.missing > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Missing</p>
                    <p className={`text-lg font-bold ${summaryStats.missing > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{formatNumber(summaryStats.missing)}</p>
                  </div>
                </div>
                <div className={`border rounded-lg px-4 py-3 flex items-center gap-3 ${summaryStats.matchPct >= 95 ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700'}`}>
                  <CheckCircle className={`h-5 w-5 flex-shrink-0 ${summaryStats.matchPct >= 95 ? 'text-green-500' : 'text-gray-400'}`} />
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Match %</p>
                    <p className={`text-lg font-bold ${summaryStats.matchPct >= 95 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>{summaryStats.matchPct.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="flex items-center justify-between">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Filter by campaign, adset, or ad name..."
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  className="pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 w-80 focus:ring-2 focus:ring-brandColor focus:border-transparent"
                />
              </div>
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
            </div>

            {/* Table */}
            <div className="background-container rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto max-h-[calc(100vh-340px)] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="sticky top-0 z-20">
                    <tr className="bg-gray-100 dark:bg-gray-800">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-100 dark:bg-gray-800 z-30">
                        Name
                      </th>
                      {hasMetaData && (
                        <th className="px-3 py-2 text-center text-xs font-medium text-blue-600 uppercase">Meta</th>
                      )}
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">CRM</th>
                      {hasMetaData && (
                        <th className="px-3 py-2 text-center text-xs font-medium text-red-500 uppercase">Miss</th>
                      )}
                      {STATUS_GROUPS.map((g) => (
                        <th key={g.key} className={`px-3 py-2 text-center text-xs font-semibold whitespace-nowrap ${g.color} ${g.headerBg}`}>
                          {g.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {groupedRows.map((row, idx) => {
                      const isCampaign = row.type === "campaign";
                      const isAdset = row.type === "adset";
                      const isClientStatus = row.type === "client_status";
                      const isGrandTotal = row.type === "grand_total";
                      const isOrganic = row.type === "organic";
                      const isOrganicStatus = row.type === "organic_status";

                      // Organic client status sub-row (mirrors FB client_status rows)
                      if (isOrganicStatus) {
                        const breakdown = row.clientStatusId ? getOrganicCrossTabBreakdown(row.clientStatusId) : [];
                        return (
                          <tr key={`os-${idx}`} className="bg-white dark:bg-gray-900">
                            <td className="pr-4 py-1 text-xs whitespace-nowrap sticky left-0 z-10 bg-white dark:bg-gray-900 pl-8 text-gray-500 dark:text-gray-400">
                              {row.name}
                            </td>
                            {hasMetaData && <td className="py-1"></td>}
                            <td className="px-3 py-1 text-xs text-center text-gray-500 dark:text-gray-400">
                              {formatNumber(row.count || 0)}
                            </td>
                            {hasMetaData && <td className="py-1"></td>}
                            {STATUS_GROUPS.map((g) => {
                              const groupCount = row.groupedCounts[g.key] || 0;
                              if (groupCount === 0) {
                                return <td key={g.key} className="px-3 py-1 text-xs text-center"></td>;
                              }
                              // Filter breakdown to only admin statuses in this group
                              const groupBreakdown = breakdown.filter((b) => {
                                if (b.name === "No Status" || b.name.startsWith("Status ")) return g.key === "new_lead";
                                const adminStatus = reportData?.admin_statuses.find((s) => s.opt_name === b.name);
                                if (!adminStatus) return g.key === "new_lead";
                                return (adminStatusGroupMap[adminStatus.opt_id] || "new_lead") === g.key;
                              });
                              return (
                                <td key={g.key} className={`px-3 py-1 text-xs text-center ${g.color} font-medium`}>
                                  <div className="inline-flex items-center justify-center gap-1">
                                    <span>{formatNumber(groupCount)}</span>
                                    {groupBreakdown.length > 0 && (
                                      <Info
                                        className="h-3 w-3 cursor-help flex-shrink-0 opacity-50 hover:opacity-100"
                                        onMouseEnter={(e) => {
                                          const rect = (e.target as HTMLElement).getBoundingClientRect();
                                          setTooltip({ x: rect.left + rect.width / 2, y: rect.top, groupKey: g.key, groupLabel: g.label, color: g.color, breakdown: groupBreakdown });
                                        }}
                                        onMouseLeave={() => setTooltip(null)}
                                      />
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }

                      // Client status sub-row: name + counts distributed across admin status group columns
                      if (isClientStatus) {
                        const breakdown = row.adId && row.clientStatusId
                          ? getCrossTabBreakdown(row.adId, row.clientStatusId)
                          : [];
                        return (
                          <tr key={`cs-${idx}`} className="bg-white dark:bg-gray-900">
                            <td className="pr-4 py-1 text-xs whitespace-nowrap sticky left-0 z-10 bg-white dark:bg-gray-900 pl-[4.5rem] text-gray-500 dark:text-gray-400">
                              {row.name}
                            </td>
                            {hasMetaData && <td className="py-1"></td>}
                            <td className="px-3 py-1 text-xs text-center text-gray-500 dark:text-gray-400">
                              {formatNumber(row.count || 0)}
                            </td>
                            {hasMetaData && <td className="py-1"></td>}
                            {STATUS_GROUPS.map((g) => {
                              const groupCount = row.groupedCounts[g.key] || 0;
                              if (groupCount === 0) {
                                return <td key={g.key} className="px-3 py-1 text-xs text-center"></td>;
                              }
                              // Filter breakdown to only admin statuses in this group
                              const groupBreakdown = breakdown.filter((b) => {
                                if (b.name === "No Status" || b.name.startsWith("Status ")) return g.key === "new_lead";
                                const adminStatus = reportData?.admin_statuses.find((s) => s.opt_name === b.name);
                                if (!adminStatus) return g.key === "new_lead";
                                return (adminStatusGroupMap[adminStatus.opt_id] || "new_lead") === g.key;
                              });
                              return (
                                <td key={g.key} className={`px-3 py-1 text-xs text-center ${g.color} font-medium`}>
                                  <div className="inline-flex items-center justify-center gap-1">
                                    <span>{formatNumber(groupCount)}</span>
                                    {groupBreakdown.length > 0 && (
                                      <Info
                                        className="h-3 w-3 cursor-help flex-shrink-0 opacity-50 hover:opacity-100"
                                        onMouseEnter={(e) => {
                                          const rect = (e.target as HTMLElement).getBoundingClientRect();
                                          setTooltip({ x: rect.left + rect.width / 2, y: rect.top, groupKey: g.key, groupLabel: g.label, color: g.color, breakdown: groupBreakdown });
                                        }}
                                        onMouseLeave={() => setTooltip(null)}
                                      />
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }

                      const missing = Math.max(0, row.metaLeads - row.total);

                      const rowBg = isOrganic
                        ? "bg-emerald-50 dark:bg-emerald-900/20"
                        : isCampaign
                        ? "bg-gray-100 dark:bg-gray-800"
                        : isAdset
                        ? "bg-gray-50 dark:bg-gray-850"
                        : isGrandTotal
                        ? "bg-blue-50 dark:bg-blue-900/20 sticky bottom-0 z-10"
                        : "bg-white dark:bg-gray-900";

                      const nameStyle = isOrganic
                        ? "font-bold text-emerald-800 dark:text-emerald-300 pl-4"
                        : isCampaign
                        ? "font-bold text-gray-900 dark:text-white pl-4"
                        : isAdset
                        ? "font-semibold text-gray-800 dark:text-gray-200 pl-8"
                        : isGrandTotal
                        ? "font-bold text-blue-800 dark:text-blue-300 pl-4"
                        : "text-gray-700 dark:text-gray-300 pl-14";

                      const numStyle = isOrganic
                        ? "font-bold text-emerald-800 dark:text-emerald-300"
                        : isGrandTotal
                        ? "font-bold text-blue-800 dark:text-blue-300"
                        : isCampaign
                        ? "font-bold text-gray-900 dark:text-white"
                        : isAdset
                        ? "font-semibold text-gray-800 dark:text-gray-200"
                        : "text-gray-600 dark:text-gray-400";

                      const showGroupedCounts = isOrganic || isGrandTotal;

                      return (
                        <tr key={`${row.type}-${idx}`} className={`${rowBg} hover:bg-gray-100/50 dark:hover:bg-gray-800/50`}>
                          <td className={`pr-4 py-2 text-sm whitespace-nowrap sticky left-0 z-10 ${rowBg} ${nameStyle}`}>
                            {row.name}
                          </td>
                          {hasMetaData && (
                            <td className={`px-3 py-2 text-sm text-center ${numStyle}`}>
                              {row.metaLeads > 0 ? formatNumber(row.metaLeads) : "-"}
                            </td>
                          )}
                          <td className={`px-3 py-2 text-sm text-center ${numStyle}`}>
                            {formatNumber(row.total)}
                          </td>
                          {hasMetaData && (
                            <td className={`px-3 py-2 text-sm text-center ${isGrandTotal ? 'font-bold' : ''} ${missing > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-400 dark:text-gray-600'}`}>
                              {isOrganic ? "-" : missing > 0 ? formatNumber(missing) : "-"}
                            </td>
                          )}
                          {STATUS_GROUPS.map((g) => {
                            if (!showGroupedCounts) {
                              return <td key={g.key} className="px-3 py-2 text-sm text-center"></td>;
                            }
                            const groupCount = row.groupedCounts[g.key] || 0;
                            return (
                              <td key={g.key} className={`px-3 py-2 text-sm text-center ${numStyle}`}>
                                {groupCount > 0 ? formatNumber(groupCount) : "-"}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Fixed-position status breakdown tooltip */}
      {tooltip && (
        <div
          className="fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-xl p-2.5 min-w-[160px] pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y - 8, transform: "translate(-50%, -100%)" }}
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide mb-1.5 text-gray-500 dark:text-gray-400">Admin Statuses</p>
          {tooltip.breakdown.map((s) => (
            <div key={s.name} className="flex items-center justify-between gap-4 py-0.5">
              <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-[110px]">{s.name}</span>
              <span className={`text-xs font-semibold ${tooltip.color}`}>{formatNumber(s.count)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdLevelReport;
